import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, Package, CheckCircle, XCircle, Clock, Calendar, Award } from 'lucide-react';
import DateSelector from './DateSelector';

const API_URL = process.env.REACT_APP_API_URL;

function DriverMetricsTab({ driverData, driverId }) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Date range state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);

  function formatDateLocal(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Initialize with current month
  useEffect(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      fetchMetrics();
    }
  }, [startDate, endDate]);

  async function fetchMetrics() {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/admin/drivers/${driverId}/metrics`,
        { params: { start_date: startDate, end_date: endDate } }
      );
      setMetrics(response.data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
      alert('Error al cargar métricas');
    } finally {
      setLoading(false);
    }
  }

  function setPresetRange(preset) {
    const now = new Date();
    let start, end;

    switch (preset) {
      case 'current_month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'last_month':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'last_7_days':
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        end = now;
        break;
      case 'last_30_days':
        start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        end = now;
        break;
      default:
        return;
    }

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  }

  function formatDateLocal(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Helper function to parse YYYY-MM-DD string to Date object safely
  function parseDateString(dateStr) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  // Helper function to format date for display
  function formatDateForDisplay(dateStr) {
    const date = parseDateString(dateStr);
    return date.toLocaleDateString('es-PE', { 
      month: 'short', 
      day: 'numeric' 
    });
  }

  function getPerformanceBadge(tier, bonusEligible, extraBonusEligible) {
    if (extraBonusEligible) {
      return (
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#E6B43C] to-[#E6B43C]/80 text-white">
          <Award className="w-5 h-5" />
          <span className="font-bold font-montserrat">Extra Bonus (≥87%)</span>
        </div>
      );
    }
    if (bonusEligible) {
      return (
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#67A43C] text-white">
          <Award className="w-5 h-5" />
          <span className="font-bold font-montserrat">Bonus Elegible (83-87%)</span>
        </div>
      );
    }
    if (tier === 'satisfactory') {
      return (
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-500 text-white">
          <CheckCircle className="w-5 h-5" />
          <span className="font-bold font-montserrat">Satisfactorio (75-82%)</span>
        </div>
      );
    }
    return (
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500 text-white">
        <XCircle className="w-5 h-5" />
        <span className="font-bold font-montserrat">Bajo Rendimiento (&lt;75%)</span>
      </div>
    );
  }

  if (loading && !metrics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B1336E] mx-auto"></div>
          <p className="mt-4 text-slate-600 font-montserrat">Cargando métricas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Date Range Selector */}
      <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
        <h3 className="font-bold text-slate-800 font-montserrat mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#B1336E]" />
          Rango de Fechas
        </h3>

        {/* Preset Buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => setPresetRange('current_month')}
            className="px-4 py-2 bg-white border-2 border-slate-300 rounded-lg font-semibold font-montserrat text-slate-700 hover:border-[#B1336E] hover:text-[#B1336E] transition-colors text-sm"
          >
            Este Mes
          </button>
          <button
            onClick={() => setPresetRange('last_month')}
            className="px-4 py-2 bg-white border-2 border-slate-300 rounded-lg font-semibold font-montserrat text-slate-700 hover:border-[#B1336E] hover:text-[#B1336E] transition-colors text-sm"
          >
            Mes Pasado
          </button>
          <button
            onClick={() => setPresetRange('last_7_days')}
            className="px-4 py-2 bg-white border-2 border-slate-300 rounded-lg font-semibold font-montserrat text-slate-700 hover:border-[#B1336E] hover:text-[#B1336E] transition-colors text-sm"
          >
            Últimos 7 Días
          </button>
          <button
            onClick={() => setPresetRange('last_30_days')}
            className="px-4 py-2 bg-white border-2 border-slate-300 rounded-lg font-semibold font-montserrat text-slate-700 hover:border-[#B1336E] hover:text-[#B1336E] transition-colors text-sm"
          >
            Últimos 30 Días
          </button>
        </div>

        {/* Custom Date Selectors */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DateSelector
            selectedDate={startDate}
            onDateChange={setStartDate}
            showCalendar={showStartCalendar}
            onToggleCalendar={() => setShowStartCalendar(!showStartCalendar)}
          />
          <DateSelector
            selectedDate={endDate}
            onDateChange={setEndDate}
            showCalendar={showEndCalendar}
            onToggleCalendar={() => setShowEndCalendar(!showEndCalendar)}
          />
        </div>
      </div>

      {metrics && (
        <>
          {/* Performance Badge */}
          <div className="flex justify-center">
            {getPerformanceBadge(
              metrics.summary.performance_tier,
              metrics.summary.bonus_eligible,
              metrics.summary.extra_bonus_eligible
            )}
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Deliveries */}
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <Package className="w-8 h-8 text-[#B1336E]" />
                <div className="text-right">
                  <p className="text-2xl font-bold text-slate-800 font-montserrat">
                    {metrics.summary.total_deliveries}
                  </p>
                  <p className="text-xs text-slate-500 font-montserrat">Total Entregas</p>
                </div>
              </div>
            </div>

            {/* Success Rate */}
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle className="w-8 h-8 text-[#67A43C]" />
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#67A43C] font-montserrat">
                    {metrics.summary.success_rate}%
                  </p>
                  <p className="text-xs text-slate-500 font-montserrat">Tasa de Éxito</p>
                </div>
              </div>
              <div className="text-xs text-slate-600 font-montserrat">
                {metrics.summary.successful_deliveries} entregados
              </div>
            </div>

            {/* Rejected */}
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <XCircle className="w-8 h-8 text-red-500" />
                <div className="text-right">
                  <p className="text-2xl font-bold text-red-600 font-montserrat">
                    {metrics.summary.rejected_deliveries}
                  </p>
                  <p className="text-xs text-slate-500 font-montserrat">Rechazados</p>
                </div>
              </div>
            </div>

            {/* On-Time */}
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <Clock className="w-8 h-8 text-blue-500" />
                <div className="text-right">
                  <p className="text-2xl font-bold text-blue-600 font-montserrat">
                    {metrics.summary.on_time_percentage}%
                  </p>
                  <p className="text-xs text-slate-500 font-montserrat">A Tiempo</p>
                </div>
              </div>
              <div className="text-xs text-slate-600 font-montserrat">
                {metrics.summary.on_time_deliveries} de {metrics.summary.successful_deliveries}
              </div>
            </div>
          </div>

          {/* Average Deliveries Per Day */}
          <div className="bg-gradient-to-r from-[#B1336E] to-[#B1336E]/80 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold font-montserrat mb-1 opacity-90">
                  Promedio de Entregas por Día
                </p>
                <p className="text-4xl font-bold font-montserrat">
                  {metrics.summary.avg_deliveries_per_day}
                </p>
              </div>
              <TrendingUp className="w-12 h-12 opacity-50" />
            </div>
            <p className="text-xs mt-3 opacity-75 font-montserrat">
              Basado en {metrics.period.days} día(s) de actividad
            </p>
          </div>

          {/* Daily Trends Chart */}
          {metrics.daily_trends.length > 0 && (
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="font-bold text-slate-800 font-montserrat mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#B1336E]" />
                Tendencia Diaria
              </h3>

              {/* Simple Bar Chart */}
              <div className="space-y-3">
      {metrics.daily_trends.map((day) => {
        const successPercentage = day.success_rate;
        const barColor = 
          successPercentage >= 87 ? '#E6B43C' :
          successPercentage >= 83 ? '#67A43C' :
          successPercentage >= 75 ? '#64748b' :
          '#ef4444';

        return (
          <div key={day.date} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="font-semibold text-slate-700 font-montserrat">
                {formatDateForDisplay(day.date)}
              </span>
              <div className="flex items-center gap-3">
                <span className="text-slate-600 font-montserrat text-xs">
                  {day.total_deliveries} entregas
                </span>
                <span 
                  className="font-bold font-montserrat w-14 text-right"
                  style={{ color: barColor }}
                >
                  {day.success_rate}%
                </span>
              </div>
            </div>
            <div className="relative h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="absolute top-0 left-0 h-full rounded-full transition-all duration-300"
                style={{
                  width: `${day.success_rate}%`,
                  backgroundColor: barColor
                }}
              ></div>
            </div>
          </div>
        );
      })}
    </div>

              {/* Legend */}
              <div className="mt-6 pt-4 border-t border-slate-200">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide font-montserrat mb-2">
                  Leyenda
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs font-montserrat">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <span className="text-slate-600">&lt;75% - Bajo</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-slate-500"></div>
                    <span className="text-slate-600">75-82% - Satisfactorio</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#67A43C]"></div>
                    <span className="text-slate-600">83-87% - Bonus</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#E6B43C]"></div>
                    <span className="text-slate-600">≥87% - Extra Bonus</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Period Info */}
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <p className="text-sm text-slate-600 font-montserrat">
              <span className="font-semibold">Período:</span>{' '}
              {formatDateForDisplay(metrics.period.start_date)} -{' '}
              {formatDateForDisplay(metrics.period.end_date)}{' '}
              ({metrics.period.days} días)
            </p>
          </div>
        </>
      )}
    </div>
  );
}

export default DriverMetricsTab;